import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://102.220.100.99/api';

const LeadSubmission = ({ selectedCalls = [], onClose, onSubmissionComplete }) => {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [submissionMode, setSubmissionMode] = useState('manual'); // 'manual', 'auto', or 'scheduled'
  const [activeStep, setActiveStep] = useState(0);
  const [submissionResults, setSubmissionResults] = useState([]);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [autoSubmissionSettings, setAutoSubmissionSettings] = useState({
    enabled: false,
    minQAScore: 8.0,
    campaigns: [],
    dispositions: ['SALE', 'INTERESTED'],
    requireApproval: true
  });

  const steps = ['Select Providers', 'Configure Settings', 'Review & Submit', 'Results'];

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_URL}/service-providers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch providers');
      }

      // Only show active providers
      const activeProviders = (data.data || data).filter(p => p.active);
      setProviders(activeProviders);
    } catch (err) {
      console.error('Fetch providers error:', err);
      setError(err.message || 'Failed to fetch providers');
    }
  };

  const handleProviderSelection = (providerId) => {
    setSelectedProviders(prev => {
      if (prev.includes(providerId)) {
        return prev.filter(id => id !== providerId);
      } else {
        return [...prev, providerId];
      }
    });
  };

  const handleBulkSubmission = async () => {
    if (selectedProviders.length === 0) {
      setError('Please select at least one provider');
      return;
    }

    if (selectedCalls.length === 0) {
      setError('No calls selected for submission');
      return;
    }

    if (submissionMode === 'scheduled' && !scheduledDateTime) {
      setError('Please select a date and time for scheduled submission');
      return;
    }

    if (submissionMode === 'scheduled' && new Date(scheduledDateTime) <= new Date()) {
      setError('Scheduled time must be in the future');
      return;
    }

    setIsSubmitting(true);
    setSubmissionResults([]);
    setSubmissionProgress(0);
    setError('');

    try {
      // Handle scheduled submissions
      if (submissionMode === 'scheduled') {
        const response = await fetch(`${API_URL}/scheduled-submissions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            callIds: selectedCalls.map(call => call.id),
            providerIds: selectedProviders,
            scheduledAt: scheduledDateTime,
            submissionData: {
              // Add any additional submission data here
              source: 'manual_selection',
              timestamp: new Date().toISOString()
            }
          })
        });

        const data = await response.json();
        
        if (response.ok) {
          setSuccess(`Successfully scheduled ${data.data.scheduled} submissions for ${new Date(scheduledDateTime).toLocaleString()}`);
          setActiveStep(3); // Move to results step
          
          // Create results array for display
          const results = [{
            success: true,
            status: 'scheduled',
            message: `${data.data.scheduled} submissions scheduled successfully`,
            scheduledCount: data.data.scheduled,
            errors: data.data.errors || []
          }];
          
          setSubmissionResults(results);
          
          if (onSubmissionComplete) {
            onSubmissionComplete(results);
          }
        } else {
          throw new Error(data.error || 'Failed to schedule submissions');
        }
        
        return;
      }

      // Handle immediate submissions (manual/auto)
      const totalSubmissions = selectedCalls.length * selectedProviders.length;
      let completedSubmissions = 0;
      const results = [];

      for (const call of selectedCalls) {
        for (const providerId of selectedProviders) {
          try {
            const response = await fetch(`${API_URL}/leads/submit-call/${call.id}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                providerIds: [providerId]
              })
            });

            const data = await response.json();
            
            results.push({
              callId: call.id,
              callUniqueId: call.unique_id,
              providerId,
              providerName: providers.find(p => p.id === providerId)?.name,
              success: response.ok,
              status: data.success ? 'success' : 'failed',
              error: data.error || null,
              response: data.data || null
            });

            completedSubmissions++;
            setSubmissionProgress((completedSubmissions / totalSubmissions) * 100);

            // Small delay to prevent overwhelming the APIs
            await new Promise(resolve => setTimeout(resolve, 500));

          } catch (err) {
            results.push({
              callId: call.id,
              callUniqueId: call.unique_id,
              providerId,
              providerName: providers.find(p => p.id === providerId)?.name,
              success: false,
              status: 'failed',
              error: err.message,
              response: null
            });
            
            completedSubmissions++;
            setSubmissionProgress((completedSubmissions / totalSubmissions) * 100);
          }
        }
      }

      setSubmissionResults(results);
      setActiveStep(3); // Move to results step
      
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      
      setSuccess(`Completed: ${successCount} successful, ${failureCount} failed submissions`);
      
      if (onSubmissionComplete) {
        onSubmissionComplete(results);
      }

    } catch (err) {
      setError(`${submissionMode === 'scheduled' ? 'Scheduling' : 'Bulk submission'} failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedProviders.length === 0) {
      setError('Please select at least one provider');
      return;
    }
    
    setError('');
    if (activeStep === 2) {
      // Start submission on "Submit" step
      handleBulkSubmission();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Service Providers
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Choose which providers to submit the selected calls to:
            </Typography>
            
            {providers.length === 0 ? (
              <Alert severity="warning">
                No active service providers found. Please configure providers first.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {providers.map((provider) => (
                  <Grid item xs={12} sm={6} md={4} key={provider.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedProviders.includes(provider.id) ? 2 : 1,
                        borderColor: selectedProviders.includes(provider.id) ? 'primary.main' : 'divider'
                      }}
                      onClick={() => handleProviderSelection(provider.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Checkbox
                            checked={selectedProviders.includes(provider.id)}
                            onChange={() => handleProviderSelection(provider.id)}
                          />
                          <Typography variant="h6">
                            {provider.name}
                          </Typography>
                        </Box>
                        <Typography color="text.secondary" variant="body2">
                          {provider.description || 'No description'}
                        </Typography>
                        <Chip 
                          label={provider.api_type} 
                          size="small" 
                          sx={{ mt: 1 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Submission Settings
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Submission Mode</InputLabel>
              <Select
                value={submissionMode}
                onChange={(e) => setSubmissionMode(e.target.value)}
                label="Submission Mode"
              >
                <MenuItem value="manual">Manual Submission</MenuItem>
                <MenuItem value="auto">Automatic Submission</MenuItem>
                <MenuItem value="scheduled">Scheduled Submission</MenuItem>
              </Select>
            </FormControl>

            {submissionMode === 'auto' && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Auto-Submission Rules</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={autoSubmissionSettings.enabled}
                            onChange={(e) => setAutoSubmissionSettings(prev => ({
                              ...prev,
                              enabled: e.target.checked
                            }))}
                          />
                        }
                        label="Enable automatic submission after QA analysis"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Minimum QA Score</InputLabel>
                        <Select
                          value={autoSubmissionSettings.minQAScore}
                          onChange={(e) => setAutoSubmissionSettings(prev => ({
                            ...prev,
                            minQAScore: e.target.value
                          }))}
                          label="Minimum QA Score"
                        >
                          <MenuItem value={7.0}>7.0 - Good</MenuItem>
                          <MenuItem value={8.0}>8.0 - Very Good</MenuItem>
                          <MenuItem value={9.0}>9.0 - Excellent</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={autoSubmissionSettings.requireApproval}
                            onChange={(e) => setAutoSubmissionSettings(prev => ({
                              ...prev,
                              requireApproval: e.target.checked
                            }))}
                          />
                        }
                        label="Require manual approval before submission"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {submissionMode === 'scheduled' && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Schedule Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Choose when to automatically send the lead submissions:
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Schedule Date & Time"
                        type="datetime-local"
                        value={scheduledDateTime}
                        onChange={(e) => setScheduledDateTime(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          min: new Date(Date.now() + 60000).toISOString().slice(0, 16) // Minimum 1 minute from now
                        }}
                        helperText="Select the date and time when submissions should be sent automatically"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                You are about to submit <strong>{selectedCalls.length} calls</strong> to{' '}
                <strong>{selectedProviders.length} provider(s)</strong>.
                <br />
                Total submissions: <strong>{selectedCalls.length * selectedProviders.length}</strong>
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Providers:
                </Typography>
                {selectedProviders.map(providerId => {
                  const provider = providers.find(p => p.id === providerId);
                  return (
                    <Chip 
                      key={providerId}
                      label={provider?.name}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  );
                })}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Submission Mode:
                </Typography>
                <Chip 
                  label={
                    submissionMode === 'manual' ? 'Manual Submission' : 
                    submissionMode === 'auto' ? 'Automatic Submission' : 
                    'Scheduled Submission'
                  }
                  color="primary"
                />
                {submissionMode === 'scheduled' && scheduledDateTime && (
                  <Chip 
                    label={`Scheduled for: ${new Date(scheduledDateTime).toLocaleString()}`}
                    color="secondary"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Submission Results
            </Typography>
            
            {isSubmitting && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Submitting leads... {Math.round(submissionProgress)}% complete
                </Typography>
                <LinearProgress variant="determinate" value={submissionProgress} />
              </Box>
            )}

            {submissionResults.length > 0 && (
              <>
                {submissionMode === 'scheduled' ? (
                  // Scheduled submission results
                  submissionResults.map((result, index) => (
                    <Alert key={index} severity={result.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {result.success ? 'Submissions Scheduled Successfully!' : 'Scheduling Failed'}
                      </Typography>
                      <Typography variant="body2">
                        {result.message}
                      </Typography>
                      {result.scheduledCount && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Total Scheduled:</strong> {result.scheduledCount} submissions
                        </Typography>
                      )}
                      {result.errors && result.errors.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="error">
                            <strong>Errors:</strong>
                          </Typography>
                          {result.errors.map((error, errorIndex) => (
                            <Typography key={errorIndex} variant="body2" color="error">
                              • Call {error.callId}, Provider {error.providerId}: {error.error}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Alert>
                  ))
                ) : (
                  // Immediate submission results
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Call ID</TableCell>
                          <TableCell>Provider</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Response</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {submissionResults.map((result, index) => (
                          <TableRow key={index}>
                            <TableCell>{result.callUniqueId}</TableCell>
                            <TableCell>{result.providerName}</TableCell>
                            <TableCell>
                              <Chip
                                icon={result.success ? <CheckCircleIcon /> : <ErrorIcon />}
                                label={result.status}
                                color={result.success ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {result.error || 'Success'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Lead Submission
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {getStepContent(index)}
                
                <Box sx={{ mt: 3 }}>
                  <Button
                    disabled={activeStep === 0 || isSubmitting}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                  
                  {activeStep < steps.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={isSubmitting}
                      startIcon={activeStep === 2 ? <SendIcon /> : null}
                    >
                      {activeStep === 2 ? 'Submit to Providers' : 'Next'}
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {activeStep === 3 ? 'Close' : 'Cancel'}
        </Button>
        
        {activeStep === 3 && submissionResults.length > 0 && (
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              setActiveStep(0);
              setSubmissionResults([]);
              setSubmissionProgress(0);
              setError('');
              setSuccess('');
            }}
          >
            Submit More
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeadSubmission; 